import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import Modal from 'react-modal';
import { motion } from 'framer-motion';
import {
  UilCalendarAlt, UilFacebookF, UilCheckCircle, UilStar,
  UilShieldCheck, UilAward, UilArrowRight, UilPhone
} from '@iconscout/react-unicons';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { reviewsService } from '../services/reviews/reviewsService';

import CleanPlace from '../assets/Images/CleanPlace.jpg';
import CleanHome from '../assets/Images/CleanHome.jpg';
import Bathroom from '../assets/Images/bathroom.jpg';
import Move_In from '../assets/Images/Move-In.jpg';
import Move_Out from '../assets/Images/Moving-Out.jpg';
import Office from '../assets/Images/office.jpeg';
import Logo from '../assets/Images/logo2.png';
import User from '../assets/Icons/user.svg';
import GoogleIcon from '../assets/Icons/google.png';

import Loader from '../components/Loader';
import Trusters from '../components/Trusters';
import FooterPage from '../components/FooterPage';
import Request from '../components/Request';
import { Link } from 'react-router-dom';

Modal.setAppElement('#root');

const CleaningForm = lazy(() => import('../components/CleaningForm'));
const StarRatings   = lazy(() => import('react-star-ratings'));

const services = [
  { name: 'Home Cleaning',   img: Bathroom, description: 'Regular maintenance to keep your home spotless', color: 'bg-blue-500' },
  { name: 'Deep Cleaning',   img: Move_In,  description: 'Thorough cleaning for every corner of your space', color: 'bg-emerald-500' },
  { name: 'Office Cleaning', img: Office,   description: 'Professional workspace cleaning services', color: 'bg-amber-500' },
  { name: 'Move In/Out',     img: Move_Out, description: 'Complete cleaning for transitions', color: 'bg-violet-500' },
  { name: 'Airbnb Cleaning', img: CleanHome,description: 'Fast turnarounds for rental properties', color: 'bg-rose-500' },
  { name: 'And More',        img: Logo,     description: 'Custom cleaning solutions for your needs', color: 'bg-slate-500' },
];

const features = [
  { icon: <UilCheckCircle size={28} />, title: '25+ Years Experience', description: 'Trusted by thousands since 1998' },
  { icon: <UilShieldCheck  size={28} />, title: 'Licensed & Insured',   description: 'Fully bonded for your peace of mind' },
  { icon: <UilStar         size={28} />, title: '5-Star Rated',         description: 'Top-rated on Google & Yelp' },
  { icon: <UilAward        size={28} />, title: 'Satisfaction Guaranteed', description: '100% happiness or we return' },
];

const facebookReviews = [
  { name: 'Brianne Thompson',       date: 'May 2019',     text: 'Just got a deep clean from Fabiana and her crew, and was thoroughly impressed. Very professional and detailed. We look forward to having her return every two weeks!' },
  { name: 'Marilee Conrad Cipra',   date: 'December 2018',text: "Fabiana is wonderful! We just came home to a house cleaner than we've ever seen it. She's communicative, friendly, and her work speaks for itself. Highly recommend!" },
  { name: 'Leila Toigo',            date: 'February',     text: "We have used Fabi for years and are so happy with her services. She's always thorough, on time, and fair with pricing." },
  { name: 'Barbara Winslow Chinn',  date: 'September 2018',text: "Fabiana does great work and pays attention to all the details. We have been using her company for years and have never been disappointed." },
  { name: 'Tanya Herman',           date: 'August 2018',  text: "Faby is paying attention to all the small details. She is a perfectionist and leaves the house sparkling!" },
];

const structuredData = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "Fabiana Fabulous Cleaning Services LLC",
  "image": "https://fabycleaners.com/logo5.jpg",
  "@id": "https://fabycleaners.com",
  "url": "https://fabycleaners.com",
  "telephone": "+14258296092",
  "priceRange": "$$",
  "address": { "@type": "PostalAddress", "addressLocality": "Lake Stevens", "addressRegion": "WA", "postalCode": "98258", "addressCountry": "US" },
  "geo": { "@type": "GeoCoordinates", "latitude": 47.975477, "longitude": -122.063866 },
  "openingHoursSpecification": [
    { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"], "opens": "07:00", "closes": "18:00" },
    { "@type": "OpeningHoursSpecification", "dayOfWeek": "Saturday", "opens": "07:00", "closes": "13:00" }
  ],
  "sameAs": ["https://www.facebook.com/Fabulouscleaner","https://www.instagram.com/fabianallc/","https://www.yelp.com/biz/fabiana-fabulous-cleaning-services-redmond-4"],
  "aggregateRating": { "@type": "AggregateRating", "ratingValue": "5", "reviewCount": "50" },
  "areaServed": ["Bellevue, WA","Redmond, WA","Sammamish, WA","Woodinville, WA","Kirkland, WA","Seattle, WA","Mercer Island, WA","Issaquah, WA","Bothell, WA","Edmonds, WA"]
};

export default function Home() {
  const [reviews, setReviews]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [open, setOpen]         = useState(false);

  const openModal  = useCallback(() => setOpen(true),  []);
  const closeModal = useCallback(() => setOpen(false), []);

  useEffect(() => {
    let canceled = false;
    const fetch = async () => {
      const result = await reviewsService.getReviews();
      if (!canceled && result.success) setReviews(result.data);
    };
    fetch();
    setTimeout(() => !canceled && setLoading(false), 500);
    return () => { canceled = true; };
  }, []);

  return (
    <div className="bg-white text-slate-900">
      <Helmet>
        <title>Fabiana Fabulous Cleaning Services LLC - Professional House Cleaning Seattle & Eastside</title>
        <meta name="description" content="Professional house cleaning in Seattle & Eastside since 1998. Licensed, bonded & insured. Home, office, Airbnb, move in/out cleaning. Call (425) 829-6092 for a free quote!" />
        <meta name="keywords" content="house cleaning Seattle, maid service Bellevue, cleaning service Redmond, professional cleaners Kirkland, deep cleaning Sammamish" />
        <link rel="canonical" href="https://fabycleaners.com/" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      {/* ─── Hero ─── */}
      <section className="relative min-h-screen flex items-center bg-cover bg-center" style={{ backgroundImage: `url(${CleanPlace})` }}>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-slate-900/40" />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-24 pb-16">
          <div className="grid md:grid-cols-2 gap-12 items-center">

            {/* Left */}
            <motion.div initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.9 }}>
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                <span className="inline-flex items-center gap-2 bg-gold-500/20 border border-gold-400/30 text-gold-300 text-xs font-semibold px-4 py-2 rounded-full mb-6">
                  ⭐ 5-Star Rated · 25+ Years Experience
                </span>
              </motion.div>
              <h1 className="text-5xl md:text-6xl font-bold text-white leading-tight tracking-tighter mb-6">
                Premium Cleaning for{' '}
                <span className="text-gold-400">Seattle & Eastside</span>
              </h1>
              <p className="text-lg text-white/65 mb-8 leading-relaxed max-w-lg">
                Detail-focused cleaning for luxury homes & estates since 1998. Licensed, bonded & insured.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mb-10">
                <Link to="/request"
                  className="inline-flex items-center justify-center gap-2 bg-brand-600 hover:bg-brand-700 text-white font-semibold px-7 py-4 rounded-xl transition-colors text-sm">
                  <UilCalendarAlt size={18} /> Get Free Quote
                </Link>
                <a href="tel:4258296092"
                  className="inline-flex items-center justify-center gap-2 border-2 border-white/30 hover:border-white text-white font-semibold px-7 py-4 rounded-xl transition-all text-sm">
                  <UilPhone size={18} /> (425) 829-6092
                </a>
              </div>
              <div className="flex flex-wrap gap-5 text-sm text-white/60">
                {['Licensed & Insured', 'Background Checked', 'Satisfaction Guaranteed'].map((t) => (
                  <div key={t} className="flex items-center gap-1.5">
                    <UilCheckCircle size={15} className="text-gold-400" /> {t}
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Right – Quick Estimate Card */}
            <motion.div initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.9, delay: 0.2 }}>
              <div className="bg-white rounded-3xl shadow-2xl p-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">Exclusive Offer</h3>
                    <p className="text-sm text-slate-500 mt-1">Book first regular cleaning</p>
                  </div>
                  <span className="bg-amber-100 text-amber-700 text-xs font-bold px-3 py-1.5 rounded-full">15% OFF</span>
                </div>

                <button onClick={openModal}
                  className="w-full bg-brand-600 hover:bg-brand-700 text-white font-semibold py-3.5 rounded-xl transition-colors mb-6 text-sm">
                  Get Instant Quote
                </button>

                <div className="border-t border-slate-100 pt-5">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Serving</p>
                  <div className="flex flex-wrap gap-1.5">
                    {['Bellevue','Redmond','Seattle','Kirkland','Sammamish','Woodinville','Issaquah','Bothell','Lynnwood','Edmonds'].map((c) => (
                      <span key={c} className="bg-slate-50 border border-slate-200 text-slate-600 text-xs px-2.5 py-1 rounded-lg">{c}</span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Modal */}
        {open && (
          <Suspense fallback={<Loader />}>
            <Modal isOpen onRequestClose={closeModal}
              className="bg-white rounded-3xl max-w-lg mx-auto mt-20 p-8 outline-none shadow-2xl relative"
              overlayClassName="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex justify-center z-50 px-4 overflow-y-auto">
              <button onClick={closeModal} className="absolute top-4 right-4 w-8 h-8 bg-slate-100 hover:bg-slate-200 rounded-full flex items-center justify-center text-slate-500 transition-colors">✕</button>
              <CleaningForm />
            </Modal>
          </Suspense>
        )}
      </section>

      {/* ─── Features ─── */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-14">
            <span className="inline-block bg-brand-50 text-brand-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">Why Choose Us</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">The Fabiana Difference</h2>
            <p className="text-slate-500 max-w-xl mx-auto">We transform spaces into immaculate sanctuaries you'll love coming home to.</p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, idx) => (
              <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.1 }}
                className="bg-white rounded-2xl p-7 border border-slate-100 shadow-card hover:shadow-card-hover transition-shadow">
                <div className="w-12 h-12 bg-brand-50 rounded-xl flex items-center justify-center text-brand-600 mb-5">{f.icon}</div>
                <h3 className="text-base font-bold text-slate-900 mb-1.5">{f.title}</h3>
                <p className="text-sm text-slate-500">{f.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Services ─── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <span className="inline-block bg-brand-50 text-brand-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">What We Offer</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">Our Services</h2>
            <p className="text-slate-500 max-w-xl mx-auto">From regular home maintenance to specialized deep cleans — tailored to your needs.</p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((svc, idx) => (
              <motion.div key={svc.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.07 }}
                whileHover={{ y: -4 }} className="group bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-card hover:shadow-card-hover transition-all">
                <div className="relative h-48 overflow-hidden">
                  <img src={svc.img} alt={svc.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className={`absolute top-3 left-3 ${svc.color} text-white text-xs font-bold px-2.5 py-1 rounded-lg`}>{svc.name}</div>
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-slate-900 mb-1">{svc.name}</h3>
                  <p className="text-sm text-slate-500 mb-4">{svc.description}</p>
                  <Link to="/services" className="inline-flex items-center gap-1 text-brand-600 hover:text-brand-700 text-sm font-semibold transition-colors">
                    Learn more <UilArrowRight size={16} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/services"
              className="inline-flex items-center gap-2 border-2 border-brand-600 text-brand-700 hover:bg-brand-600 hover:text-white font-semibold px-8 py-3 rounded-xl transition-all text-sm">
              View All Services <UilArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ─── CTA Banner ─── */}
      <section className="relative py-24 bg-cover bg-center" style={{ backgroundImage: `url(${CleanHome})` }}>
        <div className="absolute inset-0 bg-brand-700/90" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center text-white">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="inline-block bg-white/15 text-white text-xs font-bold px-4 py-1.5 rounded-full mb-6 uppercase tracking-wider">Book Today</span>
            <h2 className="text-4xl md:text-5xl font-bold mb-5 leading-tight">Ready for a Spotless Home?</h2>
            <p className="text-brand-100 text-lg mb-10">Join thousands of satisfied clients who trust us with their homes.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button onClick={openModal} whileHover={{ scale: 1.03 }}
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-brand-50 text-brand-700 font-bold px-8 py-4 rounded-xl transition-colors">
                <UilCalendarAlt size={18} /> Get Free Quote
              </motion.button>
              <a href="tel:4258296092"
                className="inline-flex items-center justify-center gap-2 border-2 border-white/40 hover:border-white text-white font-semibold px-8 py-4 rounded-xl transition-all">
                <UilPhone size={18} /> (425) 829-6092
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── Reviews ─── */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">

          {/* Header */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-center mb-16">
            <span className="inline-flex items-center gap-2 bg-gold-100 text-gold-700 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5">
              ★★★★★ &nbsp;5.0 Rating
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">What Our Clients Say</h2>
            <p className="text-slate-500 text-lg max-w-xl mx-auto">Real reviews from real customers who trust us with their homes.</p>
          </motion.div>

          {/* Platform tabs */}
          <div className="flex items-center justify-center gap-3 mb-12">
            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-2xl px-5 py-2.5 shadow-sm">
              <img src={GoogleIcon} alt="Google" className="w-5 h-5" />
              <span className="text-sm font-semibold text-slate-700">Google</span>
              <span className="text-xs bg-brand-50 text-brand-700 font-bold px-2 py-0.5 rounded-full">5.0</span>
            </div>
            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-2xl px-5 py-2.5 shadow-sm">
              <UilFacebookF size={18} className="text-blue-500" />
              <span className="text-sm font-semibold text-slate-700">Facebook</span>
              <span className="text-xs bg-blue-50 text-blue-700 font-bold px-2 py-0.5 rounded-full">5.0</span>
            </div>
          </div>

          {/* Google Reviews carousel */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-8 justify-center">
              <img src={GoogleIcon} alt="Google" className="w-4 h-4" />
              <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Google Reviews</span>
            </div>
            {loading ? <Loader /> : (
              <Carousel showArrows infiniteLoop autoPlay interval={7000} transitionTime={500}
                showThumbs={false} showStatus={false} swipeable emulateTouch className="max-w-2xl mx-auto">
                {reviews.sort((a, b) => new Date(b.time) - new Date(a.time)).slice(0, 6).map((r, i) => (
                  <div key={i} onClick={() => window.open('https://search.google.com/local/reviews?placeid=ChIJPQvSjqGtmlQRz6fzjvrwzwk', '_blank')}
                    className="bg-white rounded-3xl p-8 mx-3 mb-10 cursor-pointer shadow-card hover:shadow-card-hover transition-shadow text-left group">
                    {/* Quote mark */}
                    <div className="text-5xl font-serif text-gold-200 leading-none mb-2 select-none">"</div>
                    <p className="text-slate-700 text-base leading-relaxed mb-6">{r.text}</p>
                    <div className="flex items-center justify-between border-t border-slate-100 pt-5">
                      <div className="flex items-center gap-3">
                        <img src={r.profile_photo_url || User} alt={r.author_name}
                          referrerPolicy="no-referrer"
                          onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = User; }}
                          className="w-11 h-11 rounded-full object-cover ring-2 ring-slate-100" />
                        <div>
                          <p className="font-bold text-slate-900 text-sm">{r.author_name}</p>
                          <p className="text-xs text-slate-400">{r.relative_time_description}</p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Suspense fallback={<span className="text-gold-500 text-sm">★★★★★</span>}>
                          <StarRatings rating={r.rating} starRatedColor="#c9a050" numberOfStars={5} name={`g-${i}`} starDimension="14px" starSpacing="1px" />
                        </Suspense>
                        <img src={GoogleIcon} alt="Google" className="w-4 h-4 opacity-50 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  </div>
                ))}
              </Carousel>
            )}
          </div>

          {/* Divider */}
          <div className="flex items-center gap-4 max-w-2xl mx-auto mb-8">
            <div className="flex-1 h-px bg-slate-200" />
            <UilFacebookF size={16} className="text-blue-400 shrink-0" />
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* Facebook Reviews carousel */}
          <div className="mb-14">
            <div className="flex items-center gap-2 mb-8 justify-center">
              <UilFacebookF size={14} className="text-blue-500" />
              <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Facebook Reviews</span>
            </div>
            <Carousel showArrows infiniteLoop autoPlay interval={8000} transitionTime={500}
              showThumbs={false} showStatus={false} swipeable emulateTouch className="max-w-2xl mx-auto">
              {facebookReviews.map((rev, i) => (
                <div key={i} className="mx-3 mb-10">
                  <div className="bg-white rounded-3xl p-8 shadow-card text-left">
                    <div className="text-5xl font-serif text-blue-100 leading-none mb-2 select-none">"</div>
                    <p className="text-slate-700 text-base leading-relaxed mb-6">{rev.text}</p>
                    <div className="flex items-center justify-between border-t border-slate-100 pt-5">
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 bg-blue-500 rounded-full flex items-center justify-center shrink-0">
                          <UilFacebookF size={18} className="text-white" />
                        </div>
                        <div>
                          <p className="font-bold text-slate-900 text-sm">{rev.name}</p>
                          <p className="text-xs text-slate-400">{rev.date}</p>
                        </div>
                      </div>
                      <div className="text-gold-500 text-sm font-bold tracking-wider">★★★★★</div>
                    </div>
                  </div>
                </div>
              ))}
            </Carousel>
          </div>

          {/* Review CTA */}
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="relative overflow-hidden bg-brand-700 rounded-3xl p-10 text-center">
            <div className="absolute inset-0 opacity-5 pointer-events-none select-none flex items-center justify-center text-[200px] font-serif text-white leading-none">"</div>
            <p className="text-gold-300 text-xs font-bold uppercase tracking-widest mb-3">Share Your Experience</p>
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">Happy with our service?</h3>
            <p className="text-brand-200 mb-8 max-w-md mx-auto">A quick review helps other families find quality cleaning they can trust.</p>
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <a href="https://www.facebook.com/Fabulouscleaner/reviews" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-semibold px-6 py-3 rounded-2xl transition-colors text-sm">
                <UilFacebookF size={17} /> Review on Facebook
              </a>
              <a href="https://search.google.com/local/writereview?placeid=ChIJPQvSjqGtmlQRz6fzjvrwzwk" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-50 text-slate-800 font-semibold px-6 py-3 rounded-2xl transition-colors text-sm">
                <img src={GoogleIcon} alt="Google" className="w-4 h-4" /> Review on Google
              </a>
            </div>
          </motion.div>

        </div>
      </section>

      {/* ─── Trust Section ─── */}
      <Trusters />

      {/* ─── Final CTA ─── */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="inline-block bg-brand-500/15 text-gold-400 text-xs font-bold px-4 py-1.5 rounded-full mb-6 uppercase tracking-wider">Get Started</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-5 leading-tight">
              Ready to Experience the Fabiana Difference?
            </h2>
            <p className="text-slate-400 text-lg mb-10">Contact us today for a free estimate — no obligations, just exceptional service.</p>
            <Link to="/request"
              className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white font-bold px-10 py-4 rounded-xl transition-colors text-base">
              <UilCalendarAlt size={20} /> Get Your Free Quote Now
            </Link>
          </motion.div>
        </div>
      </section>

      <Request />
      <FooterPage />
    </div>
  );
}
